import { ActiveData } from "./deprecated-adms-actions";
import { IdentityStorageDto } from "./identity-storage-dto";
import { inject, singleton, injectWithTransform } from "tsyringe";
import { Logger, LocalStorage, LoggerTransformer } from "@identity/utils";
import { InstantConfigService } from "../../InstantConfig";
import { WindowRepository } from "../../TrackingContext";
import { IdentityStorageClient } from "./identity-storage-client";

@singleton()
export class IdentityStorageService {
  constructor(
    @inject(LocalStorage) private storage: LocalStorage,
    @injectWithTransform(Logger, LoggerTransformer, "UserIdentity")
    private logger: Logger,
    @inject(WindowRepository) private windowContextRepository: WindowRepository,
    @inject(InstantConfigService)
    private instantConfigService: InstantConfigService,
    @inject(IdentityStorageClient)
    private identityStorageClient: IdentityStorageClient,
  ) {}

  async get(): Promise<IdentityStorageDto> {
    const localData = await this.getLocalData();
    if (localData && !localData.synced) {
      this.setRemote(localData);
    }
    if (!localData || this.isDeprecated(localData)) {
      return this.getRemote();
    } else {
      this.setIdentityContextVariables(localData);
      return localData;
    }
  }

  async setRemote(data: IdentityStorageDto): Promise<IdentityStorageDto> {
    try {
      await this.identityStorageClient.postData(data);
      const localData = {
        ...this.identityStorageClient.getLocalData(),
        synced: true,
      };
      this.setIdentityContextVariables(localData);
      this.identityStorageClient.setLocalData({
        ...localData,
        timestamp: Date.now(),
      });
      return localData;
    } catch (ex) {
      this.logger.error("Updating Identity LocalStorage data failed");
    }
  }

  private isDeprecated(localData: IdentityStorageDto): boolean {
    // Fallback : days * hours * minutes * seconds * milliseconds;
    const timeDifference = this.instantConfigService.get(
      "icIdentityTtl",
      7 * 24 * 60 * 60 * 1000,
    );
    const lastWeek = Date.now() - timeDifference;
    return !localData.timestamp || localData.timestamp - lastWeek < 0;
  }

  private async getLocalData() {
    let localIdentity = await this.identityStorageClient.getLocalData();
    // ToDo: Remove 1 month after migrating remote storage to IdentityStorage.
    if (!localIdentity) {
      const deprecatedPpid = this.getLocalPPID() || this.getAdmsPPID();
      if (deprecatedPpid) {
        localIdentity = await this.migrateDeprecatedIdentity(deprecatedPpid);
      }
    }
    return localIdentity;
  }

  private async migrateDeprecatedIdentity(deprecatedPPID: string) {
    const remoteData = await this.setRemote({
      ppid: deprecatedPPID,
      synced: false,
    });
    this.clearDeprecatedLocalPpid();
    return remoteData;
  }

  private getAdmsPPID(): string | null {
    const admsData = this.storage.getItem<ActiveData>(
      "silver-surfer-active-data-v2",
    ) as ActiveData;
    return admsData?.IDENTITY?.[0].payload?.identityToken;
  }

  private getLocalPPID(): string | null {
    return this.storage.getItem("ppid");
  }

  private clearDeprecatedLocalPpid() {
    this.storage.removeItem("ppid");
  }

  private async getRemote(): Promise<IdentityStorageDto> {
    const remoteData = await this.identityStorageClient.fetchData();
    this.setIdentityContextVariables(remoteData);
    this.identityStorageClient.setLocalData({
      ...remoteData,
      timestamp: Date.now(),
    });
    return remoteData;
  }

  private setIdentityContextVariables(userData: IdentityStorageDto) {
    if (userData.over18) {
      this.windowContextRepository.set("over_18", { value: "1" });
    }
  }
}
